import { Col, Row } from 'antd';
import React, { useState, useRef } from 'react'
import { FaUpload, FaDownload, FaArrowLeft, FaArrowRight } from 'react-icons/fa'
import { LoadingButton, PrimaryButton } from '../../custom_components/customButton';
import { RiCloseCircleFill } from 'react-icons/ri';
import { colorScheme } from '../../contants/variables';
import { download, uploadImage, deleteImage } from '../../stores/api_calls/upload';
import * as FileDownload from 'js-file-download'

const MAX_IMAGES = 9;
const MAX_FILE_SIZE_MB = 10;

let slotKeySequence = 0;
const nextSlotKey = () => `upload-${++slotKeySequence}-${Date.now()}`;

/**
 * Single ordered image list with immediate-upload semantics.
 * Slot shape: { key, _id, url, preview, uploading } — array order IS the
 * display order sent to the API as product_image_id on submit.
 */
const UploadCarImages = (props) => {

    const fileUploader = useRef(null)
    const { isEdit, selectedFiles, setSelectedFiles, form, initialImageIds } = props
    const [error, setError] = useState({ isError: false, message: "" })
    const [downloading, setDownloading] = useState(false)

    const enableUploader = selectedFiles.length < MAX_IMAGES;

    const handleOpenGallery = event => {
        fileUploader.current.click();
    };

    const updateSlot = (key, patch) => {
        setSelectedFiles((previous) =>
            previous.map((slot) => (slot.key === key ? { ...slot, ...patch } : slot))
        );
    };

    const removeSlot = (key) => {
        setSelectedFiles((previous) => previous.filter((slot) => slot.key !== key));
    };

    const uploadSlot = async (slot, orderNumber) => {
        try {
            // Provisional 1-based order; the authoritative order is the
            // product_image_id array sent when the ad is posted.
            const res = await uploadImage(slot.file, orderNumber, true, form.direct_seller);
            const data = res?.data?.data;

            if (data?._id) {
                updateSlot(slot.key, {
                    _id: data._id,
                    url: data?.metadata?.image_url ?? "",
                    uploading: false,
                    file: null,
                });
            } else {
                throw new Error("Upload failed");
            }
        } catch (err) {
            removeSlot(slot.key);
            setError({
                isError: true,
                message: err?.response?.data?.server_response ?? "Image upload failed. Please try again.",
            });
        }
    };

    // Uploads immediately on selection; slots only ever hold the uploaded
    // image's _id and S3 url once done — never a File object.
    const handleImageChange = (e) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files)

            if (selectedFiles.length + filesArray.length > MAX_IMAGES) {
                setError({ isError: true, message: `You only need to upload ${MAX_IMAGES} images per bike ad.` })
                e.target.value = "";
                return;
            }

            const oversized = filesArray.find((file) => file.size / 1048576 > MAX_FILE_SIZE_MB);
            if (oversized) {
                setError({ isError: true, message: `"${oversized.name}" exceeds the maximum size of ${MAX_FILE_SIZE_MB}MB per image.` })
                e.target.value = "";
                return;
            }

            setError({ isError: false, message: "" })

            const newSlots = filesArray.map((file) => ({
                key: nextSlotKey(),
                _id: "",
                url: "",
                preview: URL.createObjectURL(file),
                uploading: true,
                file,
            }));

            setSelectedFiles([...selectedFiles, ...newSlots]);

            newSlots.forEach((slot, i) => {
                uploadSlot(slot, selectedFiles.length + i + 1);
            });

            e.target.value = "";
        }
    }

    const onRemoveImage = (slot) => {
        // Only images uploaded in this session are deleted on the server.
        // Images already attached to the listing are just removed locally —
        // the update endpoint unassigns them on submit, so cancelling the
        // edit doesn't destroy a live listing's image.
        if (slot._id && !(initialImageIds ?? []).includes(slot._id)) {
            deleteImage(slot._id).catch(() => {
                setError({ isError: true, message: "Failed to remove the image from the server." });
            });
        }

        if (slot.preview) URL.revokeObjectURL(slot.preview);
        removeSlot(slot.key);
    }

    // Swap with the adjacent slot — array order is the display order.
    const onMoveImage = (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= selectedFiles.length) return;

        setSelectedFiles((previous) => {
            const updated = [...previous];
            [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
            return updated;
        });
    };

    const handleDownload = () => {
        setDownloading(true)
        const dl = download(form.id)
        dl.then((response) => {
            if (response) {
                if (form.condition == "Used") {
                    FileDownload(response.data, `${form.plateNumber.replaceAll("-", "")}_${form.carName.replaceAll(" ", "_")}.zip`)
                } else {
                    FileDownload(response.data, `${form.fullname.replaceAll("-", "")}_${form.carName.replaceAll(" ", "_")}.zip`)
                }

                setDownloading(false)
            }
        }).catch(e => {
            console.log(e)
            setDownloading(false)
        })
    }

    return (
        <div>
            <div
                className='upload-header'
            >
                <div className='header-div'>
                    <p className='header-text'>Upload Pictures</p>
                    <p className='sub-header-text'>
                    Upload requirements: You can upload upto 9 images. Each image file size should not exceed <span style={{color:'red',fontWeight:'bold'}}>10MB </span>
                    and it's dimension should not exceed (4800 x 3600)pixels.
                    <span style={{color:'red',fontWeight:'bold'}}> Please noted that only PNG & JPG images are allowed.</span>
                    </p>
                </div>
            </div>

            {error.isError ? <p className='upload-error-text'>{error.message}</p> : ""}

            <Row>
                {enableUploader && (
                    <Col span={8}>
                        <div className='upload-container col-div' onClick={handleOpenGallery}>
                            <FaUpload/>
                            <p>UPLOAD MULTIPLE PHOTOS</p>
                        </div>

                        <input
                            ref={fileUploader}
                            id="getFile"
                            type="file"
                            hidden
                            multiple
                            onChange={handleImageChange}
                            accept=".jpg, .png, .jpeg"
                        />
                    </Col>
                )}
            </Row>

            <div
                className='upload-header'
            >
                <div className='header-div'>
                    <p className='header-text'>Images (display order)</p>
                </div>
                {isEdit ?
                    <div className='download-div'>
                        {downloading ?
                            <LoadingButton label="Downloading Images"/>
                            : <PrimaryButton onClick={handleDownload} color={colorScheme.green} label="Download All Image" Icon={<FaDownload style={{marginRight:'10px'}} />}/>
                        }
                    </div>
                : null}
            </div>

            <Row>
                {selectedFiles.map((slot, i) =>
                    <Col span={8} key={slot.key}>
                        <div className='image-container col-div'>
                            {slot.uploading && (
                                <span style={{ position: "absolute", fontSize: 18, fontWeight: "bold", zIndex: 1 }}>
                                    Uploading...
                                </span>
                            )}
                            <div
                                className='removeIcon'
                                onClick={() => onRemoveImage(slot)}
                            >
                                <RiCloseCircleFill/>
                            </div>
                            <img
                                src={slot.url || slot.preview}
                                style={{ opacity: slot.uploading ? 0.4 : 1 }}
                            />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '4px 16px 12px 16px',
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => onMoveImage(i, -1)}
                                disabled={i === 0 || slot.uploading}
                                style={{ cursor: i === 0 ? 'not-allowed' : 'pointer', padding: '4px 12px' }}
                                title="Move image left"
                            >
                                <FaArrowLeft/>
                            </button>
                            <span style={{ fontWeight: 'bold' }}>{i + 1}</span>
                            <button
                                type="button"
                                onClick={() => onMoveImage(i, 1)}
                                disabled={i === selectedFiles.length - 1 || slot.uploading}
                                style={{ cursor: i === selectedFiles.length - 1 ? 'not-allowed' : 'pointer', padding: '4px 12px' }}
                                title="Move image right"
                            >
                                <FaArrowRight/>
                            </button>
                        </div>
                    </Col>
                )}
            </Row>
        </div>
    )
}

export default UploadCarImages
